import 'isomorphic-fetch';
import { queryString } from 'shared/util/query';
import { authRequest, createFormPost, createPost, getJsonBody } from '@/api/apiHelpers';
import type { Filters } from '@/domain';
import { type CancellationPolicy, createCancellationPolicies } from '@/models/cancellationPolicy';
import { apiListAdImageWrapper } from '@/utils/urlPrepender';

const requestOptions: RequestInit = { credentials: 'include' };

/* FHH API fetch functions: */

export const fetchResults = (apiExternalUrl: string, filters: Filters = {}) => {
    const { destination: _unused, ...filtersWithoutDestination } = filters;
    return fetch(`${apiExternalUrl}/list${queryString(filtersWithoutDestination)}`, requestOptions)
        .then(getJsonBody)
        .then(apiListAdImageWrapper);
};

export const fetchMapResults = (apiExternalUrl: string, filters: Filters = {}) => {
    const { destination: _unused, ...filtersWithoutDestination } = filters;
    const url = `${apiExternalUrl}/map-list${queryString(filtersWithoutDestination)}`;
    return fetch(url, requestOptions).then(getJsonBody);
};

export const postSaveSearch = (apiExternalUrl: string, name, notify, filters: Filters = {}) => {
    const { destination: _unused, ...filtersWithoutDestination } = filters;
    return fetch(
        `${apiExternalUrl}/save-search/save`,
        createPost({
            savedSearchName: name,
            mailNotifications: notify,
            webNotifications: false,
            searchParams: queryString(filtersWithoutDestination, false),
        }),
    ).then(getJsonBody);
};

export const fetchStreetNamesFromWord = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/address/street${queryString(data)}`, { credentials: 'include' }).then(getJsonBody);
export const fetchStreetNumbersFromIds = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/address/streetNumbers${queryString(data)}`, { credentials: 'include' }).then(getJsonBody);

export const fetchFloorsFromDeliveryPointId = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/address/floors${queryString(data)}`, { credentials: 'include' }).then(getJsonBody);

export const fetchHouseholds = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/address/households${queryString(data)}`, { credentials: 'include' }).then(getJsonBody);

export const fetchPriceForImport = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/calendar/price${queryString(data)}`).then(getJsonBody);

export const createOrUpdatePriceInfo = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/price/info/create`, createPost(data)).then(getJsonBody);

export const postPriceOverrideData = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/price/override/create`, createPost(data)).then(getJsonBody);

export const deletePriceOverride = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/price/override/delete`, createPost(data)).then(getJsonBody);

export const fetchObjectPriceInfo = (apiExternalUrl: string, adId) => fetch(`${apiExternalUrl}/price/summary/${adId}`).then(getJsonBody);

export const updateRentalRestrictions = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/price/availability`, createPost(data)).then(getJsonBody);

export const getAllCancellationPolicies = async (apiExternalUrl: string): Promise<CancellationPolicy[]> =>
    fetch(`${apiExternalUrl}/cancellation-policy/all`).then(getJsonBody).then(createCancellationPolicies);

export const postOrderData = (apiExternalUrl: string, data) =>
    fetch(`${apiExternalUrl}/calendar/order`, createFormPost(data)).then(getJsonBody);

export const postItemInsertion = (apiExternalUrl: string, itemId, data) =>
    fetch(`${apiExternalUrl}/item/${itemId}/insertion`, createPost(data)).then(getJsonBody);

export const postBookingData = (apiExternalUrl: string, action, data) =>
    fetch(`${apiExternalUrl}/booking/${action}/${data.adId}`, createPost(data)).then(getJsonBody);

export const fetchObjectRecommendationAds = (contextPath: string, adId) =>
    fetch(`${contextPath}/api/mlt/travel-fhh/${adId}?rows=6&pos=travel-fhh-object`, requestOptions).then(getJsonBody);

export const trackFusClick = (apiExternalUrl: string, adId) =>
    fetch(`${apiExternalUrl}/fus/clicked`, createPost({ adId })).then(getJsonBody);

export const fetchCalendarPriceSsr = (apiInternalUrl: string, data) =>
    fetch(`${apiInternalUrl}/travel-api/fhh/calendar/price${queryString(data)}`).then(getJsonBody);

export const fetchPriceInfoSsr = (apiInternalUrl: string, adId, authCookies?: string) =>
    fetch(`${apiInternalUrl}/travel-api/fhh/price/info/${adId}`, authRequest(authCookies)).then(getJsonBody);

export const fetchBookingDataSsr = (apiInternalUrl: string, adId, authCookies?: string) =>
    fetch(`${apiInternalUrl}/travel-api/fhh/booking/list/${adId}`, authRequest(authCookies)).then(getJsonBody);

export const fetchProfileSsr = (profileReadUrl, loginId) => fetch(`${profileReadUrl}profile/sdrn:finn:user:${loginId}`).then(getJsonBody);

export const createItemIdForNewAd = (apiExternalUrl: string) =>
    fetch(`${apiExternalUrl}/item/create-item-id`, {
        method: 'POST',
        credentials: 'include',
    }).then(getJsonBody);

export const updateFacadeItem = async (apiExternalUrl: string, itemId, data, eTag: string): Promise<string | null> => {
    const response = await fetch(`${apiExternalUrl}/item/update/${itemId}`, {
        method: 'PATCH',
        headers: {
            'content-type': 'application/json',
            'x-requested-with': 'XMLHttpRequest',
            'If-Match': eTag,
        },
        body: data ? JSON.stringify(data) : '',
        credentials: 'include',
    });

    if (!response.ok) {
        // Reuse getJsonBody's error style for consistency
        throw new Error(`(${response.status}) ${response.statusText}: ${response.url}`);
    }
    return response.headers.get('ETag');
};

// TODO (CABC-19) add test
export const postImageData = (apiExternalUrl: string, itemId, data) =>
    fetch(`${apiExternalUrl}/image/upload`, createFormPost(data)).then(getJsonBody);

export const deleteImage = (apiExternalUrl: string, itemId: number, path: string) =>
    fetch(`${apiExternalUrl}/image/delete${queryString({ path, itemId })}`, { ...requestOptions, method: 'DELETE' });
